import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import find from 'lodash/find';
import React, { useEffect, useMemo, useState } from 'react';

import { LOB } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { StyledDiv } from '../../../../common/ConstantsStyles';
import { useCurrentMember } from '../../../../hooks/useCurrentMember';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSupportedLanguages } from '../../../../hooks/useSupportedLanguages';
import {
  getLanguageFromCookie,
  getLobFromCookie,
} from '../Internationalization/helpers';
import { Locale } from '../Internationalization/types';
import {
  languageNotSupportedModalInfoStyles,
  languageNotSupportedModalStyles,
  languageNotSupportedModalTitleStyles,
} from './LanguageNotSupported.styled';

export const LanguageNotSupportedModal = () => {
  const currentMember = useCurrentMember();
  const [isOpen, setIsOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const data = useLagoon('ui-messaging')();
  const languageModalTitle = find(data, {
    key: ConstantsLagoon.LANGUAGE_MODAL.LANGUAGE_NOT_AVAILABLE,
  });
  const languageModalContent = find(data, {
    key: ConstantsLagoon.LANGUAGE_MODAL.LANGUAGE_NOT_AVAILABLE_CONTENT,
  });
  const viewIdCardButton = find(data, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_BUTTON,
  });

  const handleViewIdCard = () => {
    window.location.href =
      '/community/dashboard/modal/id-cards?goBackOnClose=1';
  };

  const supportedLanguages = useSupportedLanguages();

  const [searchParams] = useSearchParams();
  // Support both 'locale' and 'lang' params for backward compatibility
  const urlLocale = searchParams?.get('locale') || searchParams?.get('lang');

  const lob = useMemo(() => {
    const lobFromCookie = getLobFromCookie();

    return (currentMember?.lineOfBusiness || lobFromCookie || '').toUpperCase();
  }, [currentMember?.lineOfBusiness]);

  useEffect(() => {
    let language = getLanguageFromCookie() || urlLocale;
    if (
      !language ||
      ['undefined', 'null', ''].includes(language?.toLowerCase()?.trim())
    ) {
      language = Locale.enUS;
    }
    setShowModal(!supportedLanguages.includes(language as Locale));
  }, [supportedLanguages]);

  if (!(showModal && lob === LOB.CNS)) return null;

  return (
    <React.Fragment>
      <ModalDialog
        css={languageNotSupportedModalStyles}
        data-auto-testid="language-not-supported-content-modal"
        data-testid="language-not-supported-content-modal"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        /* Title is added to prevent JS error as the component required title prop, title is hidden adding css rules for abyss-modal-header-container */
        title="Language Not Supported Modal"
      >
        <Layout.Stack
          css={{
            alignContent: 'center',
            paddingTop: 'clamp(15px, 3vh, 40px)',
            paddingBottom: 'clamp(15px, 3vh, 40px)',
            marginTop: 0,
            marginBottom: 0,
          }}
          space={15}
        >
          <Layout.Stack css={{ alignContent: 'center' }}>
            <IconBrand
              css={{ margin: '0px' }}
              icon="document_multiple"
              size="70px"
            />{' '}
            <Heading
              css={languageNotSupportedModalTitleStyles}
              data-auto-testid="heading-language-not-supported-modal"
              data-testid="heading-language-not-supported-modal"
              level={2}
              size="md"
            >
              {languageModalTitle?.message}
            </Heading>
            <StyledDiv
              css={languageNotSupportedModalInfoStyles}
              data-auto-testid="language-not-supported-content-info"
              data-testid="language-not-supported-content-info"
            >
              {languageModalContent?.message}
            </StyledDiv>
          </Layout.Stack>
          <StyledDiv css={{ paddingBottom: 0 }}>
            <Button
              css={{
                minWidth: '138px',
                maxWidth: '300px',
                height: '48px',
              }}
              data-auto-testid="language-not-supported-view-id-button"
              data-testid="language-not-supported-view-id-button"
              onClick={handleViewIdCard}
              size="$sm"
            >
              {viewIdCardButton?.message}
            </Button>
          </StyledDiv>
        </Layout.Stack>
      </ModalDialog>
    </React.Fragment>
  );
};
